// booking-create.component.ts

import {Component, OnInit, OnDestroy, Inject, PLATFORM_ID, ElementRef, ViewChild} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {CurrencyPipe, isPlatformBrowser, NgClass} from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
  ValidatorFn
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BookingService } from '../../services/booking/booking.service';
import { ServiceService } from '../../services/service/service.service';
import { AuthenticationService } from '../../../core/services/authentication/authentication.service';

import { Service } from '../../../core/entities/service/service.entity';
import { User, UserRoles } from '../../../core/entities/user/user.entity';

import { DateTime } from 'luxon';
import {HelperBannerComponent} from '../../../core/components/helper-banner/helper-banner.component';
import {AppImageComponent} from '../../../core/components/image/image';
import {MetaPixelService} from '../../../core/services/analytics/meta-pixel.service';
import {
  SegmentedControlComponent,
  SegmentedControlOption,
} from '../../../core/components/segmented-control/segmented-control.component';
import {
  DateTimePickerComponent,
  DateTimeSelection
} from '../../components/date-time-picker/date-time-picker.component';
import {InlineCarouselComponent} from '../../../core/components/inline-carousel/inline-carousel.component';
import {Photo} from '../../../core/entities/photo/photo.entity';
import {ServicePhoto} from '../../../core/entities/service/service-photo.entity';
import {AnalyticsService} from '../../../core/services/analytics/analytics.service';
import {TrackingService} from '../../../core/services/tracking/tracking.service';
import {TrackingEventName} from '../../../core/entities/tracking/tracking-event.entity';
import {FormatDurationPipe} from '../../../core/pipes/format-duration.pipe';

// --- Validators ---
function futureDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  const now = new Date();
  const selected = new Date(value);

  return selected.getTime() > now.getTime() ? null : { notInFuture: true };
}

@Component({
  selector: 'app-booking-create',
  imports: [
    TranslocoPipe,
    CurrencyPipe,
    NgClass,
    ReactiveFormsModule,
    HelperBannerComponent,
    AppImageComponent,
    SegmentedControlComponent,
    DateTimePickerComponent,
    RouterLink,
    InlineCarouselComponent,
    FormatDurationPipe
  ],
  templateUrl: './booking-create.component.html',
  standalone: true,
  styleUrl: './booking-create.component.scss'
})
export class BookingCreateComponent implements OnInit, OnDestroy {
  @ViewChild('bookingFormEl') private bookingFormEl?: ElementRef<HTMLFormElement>;
  public service: Service | null = null;
  public bookingForm!: FormGroup;
  public photographerSlug: string | null = null;
  public user: User | null = null;
  public selectedDateTime: DateTime | null = null;
  public submitting = false;
  public discount: number | null = 1;
  public hasSubmitAttempt = false;

  public paymentOptions: SegmentedControlOption[] = [];
  private static readonly PAYMENT_MODE_FULL: PaymentMode = 'full';
  private static readonly PAYMENT_MODE_DEPOSIT: PaymentMode = 'deposit';
  private hasTrackedSlotSelection = false;
  private hasTrackedPageView = false;
  private hasTrackedImagesView = false;
  private readonly isBrowser: boolean;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly serviceService: ServiceService,
    private readonly fb: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
    private readonly bookingService: BookingService,
    private readonly metaPixel: MetaPixelService,
    private readonly authenticationService: AuthenticationService,
    private readonly analytics: AnalyticsService,
    private readonly tracking: TrackingService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.user = this.authenticationService.getCurrentUser();
    this.bookingForm = this.fb.group({
      date: [null, [Validators.required, futureDateValidator]],
      hour: [null, [Validators.required]],
      minute: [null, [Validators.required]],
      price: [null, [Validators.min(0)]],
      note: [null, Validators.required],
      firstname: [this.user?.firstname ?? '', Validators.required],
      lastname: [this.user?.lastname ?? '', Validators.required],
      email: [this.user?.email ?? '', [Validators.required]],
      termsAccepted: [false, [Validators.requiredTrue]],
      paymentMode: [BookingCreateComponent.PAYMENT_MODE_FULL],
    });
  }

  ngOnInit(): void {
    this.getPhotographerSlug();
    this.loadService();
  }

  ngOnDestroy(): void {
  }

  public onDateTimeSelected(selection: DateTimeSelection): void {
    this.selectedDateTime = selection.dateTime;
    this.bookingForm.patchValue({
      date: selection.date,
      hour: selection.dateTime.hour,
      minute: selection.dateTime.minute,
    });
    this.trackSlotSelection(selection);
  }

  public onDateSelected(date: string): void {
    this.selectedDateTime = null;
    this.bookingForm.patchValue({
      date,
      hour: null,
      minute: null,
    });
  }

  // --- Booking logic ---

  public onSubmit(): void {
    if (this.submitting) {
      return;
    }

    if (this.bookingForm.invalid || !this.service) {
      this.bookingForm.markAllAsTouched();
      this.showError('marketplace.booking.create.select-date-form.errors.content');
      return;
    }

    if (!this.selectedDateTime) {
      this.showError('marketplace.booking.create.select-date-form.select-a-date.time.error');
      return;
    }

    this.submitting = true;

    const { price, note, email, firstname, lastname } = this.bookingForm.value;
    const customerFirstname = firstname ?? this.user?.firstname ?? null;
    const customerLastname = lastname ?? this.user?.lastname ?? null;

    // Build the selected time in the user's local timezone
    const selectedLocal = this.selectedDateTime.setZone(DateTime.local().zoneName);
    const selectedUtc = selectedLocal.toUTC();

    const depositPreference = this.getSelectedDepositValue();

    this.trackStartCheckout(selectedUtc, price, depositPreference);

    this.bookingService.createBooking(
      this.service.id,
      email,
      customerFirstname,
      customerLastname,
      price,
      note,
      selectedUtc,
      depositPreference
    ).subscribe({
      next: (result) => {
        this.metaPixel.trackBookingIntent({
          bookingId: result.booking.id,
          service: this.service,
          datetime: selectedUtc,
          amount: price ?? result.booking.publicPrice ?? result.booking.price ?? null,
          currency: 'EUR',
          user: this.user,
          roleHint: UserRoles.customer,
          eventId: `booking-${result.booking.id}`,
          photographerId: result.booking.service?.photographerId ?? null,
        });
        if (isPlatformBrowser(this.platformId)) {
          window.location.href = result.checkoutUrl;
        }
      },
      error: () => {
        this.showError('marketplace.booking.create.select-date-form.errors.content');
        this.submitting = false;
      }
    });
  }

  public onBookNow(): void {
    if (this.submitting) {
      return;
    }

    this.hasSubmitAttempt = true;
    this.bookingForm.markAllAsTouched();
    this.bookingForm.updateValueAndValidity({ emitEvent: false });

    if (this.bookingForm.valid && this.selectedDateTime) {
      this.onSubmit();
      return;
    }

    this.scrollToFirstInvalidField();
  }

  public isControlInvalid(controlName: string): boolean {
    const control = this.bookingForm.get(controlName);
    return !!control && control.invalid && (control.touched || this.hasSubmitAttempt);
  }

  public isDateOrTimeInvalid(): boolean {
    return this.isControlInvalid('date') || this.isTimeInvalid();
  }

  public isTimeInvalid(): boolean {
    return this.isControlInvalid('hour') || this.isControlInvalid('minute');
  }

  public shouldShowPriceErrors(): boolean {
    const control = this.bookingForm.get('price');
    if (!control || control.disabled) {
      return false;
    }
    return control.invalid && (control.touched || this.hasSubmitAttempt);
  }

  // --- Private helpers ---
  private getPhotographerSlug(): void {
    this.photographerSlug = this.route.snapshot.paramMap.get('slug');
  }

  private loadService(): void {
    const idParam = this.route.snapshot.paramMap.get('service');
    if (!idParam) return;
    const id = Number(idParam);
    this.serviceService.getService(id).subscribe({
      next: (service) => {
        this.service = service;
        this.discount = this.service.discountActive ? this.service.discount : 1;
        this.bookingForm.patchValue({
          price: ((service.priceRangeMaxPublic ?? 0) * (1 - (service.discount ?? 0) / 100)),
        });
        this.applyPriceValidators(service);
        this.refreshPaymentOptions();
        this.trackServicePageView(service);
        this.trackImagesViewed(service);
      },
      error: (error) => console.error('Failed to fetch service', error)
    });
  }

  public getMainImage(): string | null {
    return this.service?.image ?? this.service?.photos?.[0]?.url ?? null;
  }

  public getGalleryPhotos(): Photo[] {
    if (!this.service) return [];

    const gallery: Photo[] = [];

    if (this.service.image) {
      gallery.push({
        id: -1,
        url: this.service.image,
        createdAt: new Date(),
        updatedAt: null,
      });
    }

    if (this.service.photos?.length) {
      gallery.push(
        ...this.service.photos.map((p: ServicePhoto, index: number) => ({
          id: p.id ?? index,
          url: p.url,
          createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
          updatedAt: p.updatedAt ? new Date(p.updatedAt) : null,
        }))
      );
    }

    return gallery;
  }

  private showError(contentKey: string): void {
    this.toastr.error(
      this.transloco.translate(contentKey),
      this.transloco.translate('marketplace.booking.create.select-date-form.errors.title')
    );
  }

  public getFormattedServiceDescription(service: Service): string {
    const bio = service.description ?? '';
    return bio.replace(/\n/g, '<br>');
  }

  /** Add/remove validators on price based on service pricing mode */
  private applyPriceValidators(service: Service): void {
    const priceCtrl = this.bookingForm.get('price');
    if (!priceCtrl) return;

    // Base validators you may want to keep (e.g., decimal format)
    const baseValidators: ValidatorFn[] = [
      // Optional: accepts integers or decimals with , or .
      (ctrl: AbstractControl) => {
        if (ctrl.disabled || ctrl.value == null || ctrl.value === '') return null;
        const v = String(ctrl.value).trim();
        const ok = /^\d+([.,]\d{1,2})?$/.test(v);
        return ok ? null : { priceFormat: true };
      }
    ];

    if (service.priceRangeActive) {
      // User must enter a value in the allowed range
      const validators: ValidatorFn[] = [
        ...baseValidators,
        Validators.required,
        ...(service.priceRangeMinPublic != null ? [Validators.min(((service.priceRangeMinPublic ?? 0) * ((service.discount ?? 0) / 100)))] : []),
        ...(service.priceRangeMaxPublic != null ? [Validators.max(((service.priceRangeMaxPublic ?? 0) * ((service.discount ?? 0) / 100)))] : []),
      ];

      priceCtrl.enable({ emitEvent: false });
      priceCtrl.setValidators(validators);
    } else {
      // Fixed price: make the field read-only and not required
      priceCtrl.setValue(service.publicPrice ?? null, { emitEvent: false });
      priceCtrl.clearValidators();
      priceCtrl.disable({ emitEvent: false }); // prevents user edits
    }

    priceCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private refreshPaymentOptions(): void {
    this.paymentOptions = [
      {
        label: this.transloco.translate('marketplace.booking.create.payment.full.label'),
        description: this.transloco.translate('marketplace.booking.create.payment.full.description'),
        value: BookingCreateComponent.PAYMENT_MODE_FULL,
      },
    ];

    if (this.canPayDeposit()) {
      const percentage = this.service?.depositPercentage ?? 0;
      this.paymentOptions.push({
        label: this.transloco.translate('marketplace.booking.create.payment.deposit.label', { percentage }),
        description: this.transloco.translate('marketplace.booking.create.payment.deposit.description', { percentage }),
        value: BookingCreateComponent.PAYMENT_MODE_DEPOSIT,
      });
    } else {
      this.bookingForm.patchValue({ paymentMode: BookingCreateComponent.PAYMENT_MODE_FULL });
    }
  }

  public canPayDeposit(): boolean {
    return this.service !== null
      && this.service.allowDeposit === true
      && this.service.depositPercentage !== null
      && this.service.depositPercentage >= 10
      && this.service.depositPercentage <= 90;
  }

  private scrollToFirstInvalidField(): void {
    if (!this.isBrowser) {
      return;
    }
    const formEl = this.bookingFormEl?.nativeElement;
    if (!formEl) {
      return;
    }
    const invalidElement = formEl.querySelector('.input-group--error, .ng-invalid');
    if (invalidElement instanceof HTMLElement) {
      const rect = invalidElement.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
      if (!inView) {
        invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  public onPaymentModeChange(value: string | number): void {
    if (typeof value !== 'string') {
      return;
    }

    if (
      value === BookingCreateComponent.PAYMENT_MODE_DEPOSIT &&
      !this.canPayDeposit()
    ) {
      return;
    }

    this.bookingForm.patchValue({ paymentMode: value as PaymentMode });
  }

  private getSelectedDepositValue(): number | null {
    const paymentMode = this.bookingForm.get('paymentMode')?.value as PaymentMode;
    if (
      paymentMode === BookingCreateComponent.PAYMENT_MODE_DEPOSIT &&
      this.canPayDeposit()
    ) {
      return this.service?.depositPercentage ?? null;
    }

    return null;
  }

  public get activeLocale(): string {
    return this.transloco.getActiveLang();
  }

  private trackServicePageView(service: Service): void {
    if (this.hasTrackedPageView || !this.photographerSlug) return;
    const priceMode: 'range' | 'fixed' = service.priceRangeActive ? 'range' : 'fixed';
    const payload = {
      service_id: service.id,
      service_name: service.name,
      photographer_slug: this.photographerSlug,
      price_mode: priceMode,
      deposit_available: !!service.allowDeposit && !!service.depositPercentage,
    };
    this.tracking.track(TrackingEventName.SERVICE_PAGE_VIEW, payload, {
      gaEventName: 'service_page_view',
      flow: 'service_page',
    });
    this.hasTrackedPageView = true;
  }

  private trackSlotSelection(selection: DateTimeSelection): void {
    if (this.hasTrackedSlotSelection || !this.service || !this.photographerSlug) return;
    const datetime = selection.dateTime.toUTC().toISO();
    if (!datetime) return;
    const payload = {
      service_id: this.service.id,
      service_name: this.service.name,
      photographer_slug: this.photographerSlug,
      datetime,
    };
    this.analytics.event('select_slot', payload);
    this.tracking.track(TrackingEventName.SERVICE_PAGE_TIMESLOT_SELECTED, payload, {
      gaEventName: 'service_page_timeslot_selected',
      flow: 'service_page',
    });
    this.hasTrackedSlotSelection = true;
  }

  private trackBookingSummary(service: Service): void {
  }

  private trackStartCheckout(selectedUtc: DateTime, price: number|null, depositPreference: number|null): void {
    if (!this.service || !this.photographerSlug) return;
    const datetime = selectedUtc.toISO();
    if (!datetime) return;
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    const safePrice = numericPrice != null && !Number.isNaN(Number(numericPrice)) ? Number(numericPrice) : null;
    const paymentMode: 'full' | 'deposit' = depositPreference ? 'deposit' : 'full';
    const payload = {
      service_id: this.service.id,
      service_name: this.service.name,
      photographer_slug: this.photographerSlug,
      datetime,
      price: safePrice ?? this.service.publicPrice ?? null,
      payment_mode: paymentMode,
      deposit_percentage: depositPreference,
      currency: 'EUR',
    };
    this.analytics.event('start_checkout', payload);
    this.tracking.track(TrackingEventName.SERVICE_PAGE_DETAILS_SUBMITTED, payload, {
      gaEventName: 'service_page_details_submitted',
      flow: 'service_page',
    });
    this.tracking.track(TrackingEventName.CHECKOUT_STARTED, payload, {
      gaEventName: 'checkout_started',
      idempotencyKey: `service:${this.service.id}:CHECKOUT:${datetime}`,
      flow: 'service_page',
    });
  }

  private trackImagesViewed(service: Service): void {
    if (this.hasTrackedImagesView || !service.photos?.length || !this.photographerSlug) return;
    this.tracking.track(TrackingEventName.SERVICE_PAGE_IMAGES_VIEWED, {
      service_id: service.id,
      service_name: service.name,
      photographer_slug: this.photographerSlug,
      images_count: service.photos.length,
    }, {
      gaEventName: 'service_page_images_viewed',
      flow: 'service_page',
    });
    this.hasTrackedImagesView = true;
  }
}

type PaymentMode = 'full' | 'deposit';
